import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, ChevronLeft, ChevronRight, Download, Eye, FileCheck, FileSignature,
  Grid3X3, List, Loader2, MoreVertical, Plus, RefreshCw, ScrollText,
  Search, Shield, ShieldCheck, Tag, Trash2, Upload, Utensils, X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import DocumentCard from "./DocumentCard";
import { TabelleDieteticheModal } from "../TabelleDieteticheModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUILTIN_LABELS = {
  regolamento: "Regolamento",
  carta_servizi: "Carta Servizi",
  contratto: "Contratto",
  licenza: "Licenza",
  assicurazione: "Assicurazione",
};

export default function DocumentiStrutturaTab({ token, hasPermission }) {
  const [showTabelle, setShowTabelle] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docModalType, setDocModalType] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [categoriaFilter, setCategoriaFilter] = useState("all");

  const labelsMap = useMemo(() => {
    const m = { ...BUILTIN_LABELS };
    customCategories.forEach((c) => { m[c.category_key] = c.label; });
    return m;
  }, [customCategories]);

  const fetchDocs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [docsRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents/struttura`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/documents/struttura/categorie-personalizzate`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDocuments(docsRes.data.documents || []);
      setCustomCategories(catRes.data?.categories || []);
    } catch {
      toast.error("Errore caricamento documenti struttura");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const tiles = useMemo(() => {
    const base = [
      { key: "tabelle", title: "Tabelle Dietetiche", description: "Piani alimentari e riferimenti nutrizionali per gli ospiti.", Icon: Utensils, iconWrap: "bg-emerald-50 text-emerald-600 ring-emerald-100", onOpen: () => setShowTabelle(true) },
      { key: "regolamento", title: "Regolamento", description: "Norme interne e organizzazione della struttura.", Icon: ScrollText, iconWrap: "bg-blue-50 text-blue-600 ring-blue-100", docType: "regolamento" },
      { key: "carta_servizi", title: "Carta Servizi", description: "Servizi offerti, costi e diritti degli ospiti.", Icon: FileCheck, iconWrap: "bg-sky-50 text-sky-600 ring-sky-100", docType: "carta_servizi" },
      { key: "contratto", title: "Contratto", description: "Modelli e allegati contrattuali.", Icon: FileSignature, iconWrap: "bg-teal-50 text-teal-600 ring-teal-100", docType: "contratto" },
      { key: "licenza", title: "Licenza", description: "Autorizzazioni e titoli abilitativi.", Icon: Shield, iconWrap: "bg-rose-50 text-rose-600 ring-rose-100", docType: "licenza" },
      { key: "assicurazione", title: "Assicurazione", description: "Polizze RC e coperture della struttura.", Icon: ShieldCheck, iconWrap: "bg-cyan-50 text-cyan-600 ring-cyan-100", docType: "assicurazione" },
    ];
    const custom = customCategories.map((c) => ({
      key: c.category_key,
      title: c.label,
      description: "Categoria personalizzata per i documenti della struttura.",
      Icon: Tag,
      iconWrap: "bg-violet-50 text-violet-600 ring-violet-100",
      docType: c.category_key,
      isCustom: true,
      categoryKey: c.category_key,
    }));
    return [...base, ...custom];
  }, [customCategories]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return tiles.filter((t) => {
      if (categoriaFilter !== "all" && t.key !== categoriaFilter) return false;
      if (!q) return true;
      return t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    });
  }, [tiles, search, categoriaFilter]);

  const openModal = (type) => { setDocModalType(type); setShowDocModal(true); fetchDocs(); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docModalType) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("Max 50 MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("descrizione", "");
    try {
      await axios.post(`${API_URL}/api/documents/struttura/${docModalType}`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato");
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.file_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/documents/struttura/${doc.file_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminato");
      fetchDocs();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const handlePreview = async (doc) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/documents/struttura/${doc.doc_type}/${doc.file_id}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      setDocPreviewUrl(url);
    } catch {
      toast.error("Impossibile aprire il documento");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/documents/struttura/${doc.doc_type}/${doc.file_id}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "documento";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile scaricare il documento");
    }
  };

  const handleCreateCategory = async () => {
    if (newCatLabel.trim().length < 2) { toast.error("Nome minimo 2 caratteri"); return; }
    setCreatingCat(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/documents/struttura/categorie-personalizzate`, { label: newCatLabel.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Categoria creata");
      setShowNewCat(false);
      setNewCatLabel("");
      await fetchDocs();
      if (data?.category_key) openModal(data.category_key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setCreatingCat(false);
    }
  };

  const docsForModal = documents.filter((d) => d.doc_type === docModalType);
  const nDocsForType = (type) => documents.filter((d) => d.doc_type === type).length;

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600 shadow-sm ring-1 ring-teal-100">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Documenti Struttura</h2>
            <p className="text-sm text-slate-500">Regolamento, carta servizi, contratti e documenti interni.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1 sm:flex-none">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca documenti..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 rounded-xl pl-9" />
          </div>
          <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
            <SelectTrigger className="h-10 w-[180px] rounded-xl"><SelectValue placeholder="Tutte le categorie" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {tiles.map((t) => (
                <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={fetchDocs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <Button type="button" variant={viewMode === "grid" ? "default" : "ghost"} size="sm" className={`h-8 rounded-md px-2 ${viewMode === "grid" ? "bg-[#00A86B] text-white" : ""}`} onClick={() => setViewMode("grid")}><Grid3X3 className="h-4 w-4" /></Button>
            <Button type="button" variant={viewMode === "list" ? "default" : "ghost"} size="sm" className={`h-8 rounded-md px-2 ${viewMode === "list" ? "bg-[#00A86B] text-white" : ""}`} onClick={() => setViewMode("list")}><List className="h-4 w-4" /></Button>
          </div>
          {hasPermission("documento:upload") && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="h-10 rounded-xl bg-[#00A86B] hover:bg-[#00915d]"><Plus className="mr-2 h-4 w-4" />Carica documento</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setShowTabelle(true)}>Tabelle dietetiche</DropdownMenuItem>
                <DropdownMenuSeparator />
                {Object.entries(BUILTIN_LABELS).map(([k, v]) => (
                  <DropdownMenuItem key={k} onClick={() => openModal(k)}>{v}</DropdownMenuItem>
                ))}
                {customCategories.map((c) => (
                  <DropdownMenuItem key={c.category_key} onClick={() => openModal(c.category_key)}>{c.label}</DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowNewCat(true)}>Nuova categoria…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#00A86B]" /></div>
      ) : (
        <div className={`grid gap-3 ${viewMode === "grid" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" : "grid-cols-1"}`}>
          {filtered.map((tile) => {
            const nDocs = tile.docType ? nDocsForType(tile.docType) : 0;
            const canRemove = tile.isCustom && nDocs === 0;
            const menu = tile.docType ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openModal(tile.docType)}>Modifica / Sostituisci file</DropdownMenuItem>
                  {canRemove && (
                    <DropdownMenuItem className="text-red-600" onClick={async () => {
                      try {
                        await axios.delete(`${API_URL}/api/documents/struttura/categorie-personalizzate/${tile.categoryKey}`, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Categoria eliminata");
                        fetchDocs();
                      } catch { toast.error("Impossibile eliminare"); }
                    }}>Elimina categoria</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null;

            return (
              <DocumentCard
                key={tile.key}
                icon={tile.Icon}
                iconWrap={tile.iconWrap}
                title={tile.title}
                description={tile.description}
                footerLeft={tile.docType ? (nDocs > 0 ? `PDF · ${nDocs} file` : "PDF") : "PDF"}
                footerRight={
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-600" onClick={(e) => { e.stopPropagation(); tile.onOpen ? tile.onOpen() : openModal(tile.docType); }}>
                    Gestisci
                  </Button>
                }
                menu={menu}
                onClick={() => tile.onOpen ? tile.onOpen() : openModal(tile.docType)}
                testId={`struttura-card-${tile.key}`}
              />
            );
          })}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm text-slate-600">
        I documenti struttura sono visibili al personale autorizzato. Usa <strong>Gestisci</strong> per caricare, visualizzare o sostituire i file.
      </div>

      <TabelleDieteticheModal open={showTabelle} onOpenChange={setShowTabelle} />

      <Dialog open={showNewCat} onOpenChange={setShowNewCat}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nuova categoria</DialogTitle></DialogHeader>
          <Input value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)} placeholder="Nome categoria" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCat(false)}>Annulla</Button>
            <Button onClick={handleCreateCategory} disabled={creatingCat}>{creatingCat && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!docPreviewUrl} onOpenChange={(v) => { if (!v) { URL.revokeObjectURL(docPreviewUrl); setDocPreviewUrl(null); } }}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Anteprima documento</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden px-4 pb-4">
            {docPreviewUrl && (
              <iframe src={docPreviewUrl} className="w-full h-full rounded-lg border" title="Anteprima" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{labelsMap[docModalType] || "Documenti"}</DialogTitle>
            <DialogDescription>Gestisci i documenti di questa categoria.</DialogDescription>
          </DialogHeader>
          {hasPermission("documento:upload") && (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-6 hover:border-[#00A86B]">
              <Upload className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-600">{uploading ? "Caricamento…" : "Carica PDF o documento"}</span>
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.doc,.docx" />
            </label>
          )}
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {docsForModal.map((doc) => (
              <div key={doc.file_id} className="flex items-center justify-between rounded-lg border p-3">
                <span className="truncate text-sm">{doc.file_name}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" title="Apri" onClick={() => handlePreview(doc)}><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" title="Scarica" onClick={() => handleDownload(doc)}><Download className="h-4 w-4" /></Button>
                  {hasPermission("documento:delete") && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Elimina" onClick={() => handleDelete(doc)}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
            {docsForModal.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Nessun documento caricato</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
